import React, { useMemo, useState } from 'react';
import {
  Box, Paper, Typography, FormControl, InputLabel, Select, MenuItem, Button, Chip, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  IconButton, Tooltip, OutlinedInput, ListItemText, Checkbox, Link, Collapse,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';

// Modern Color Schemes
export const WORK_ITEM_COLORS = {
  'Epic': '#5D3FD3',          // Royal Purple
  'Feature': '#4169E1',       // Royal Blue
  'Product Backlog Item': '#00C49F', // Teal
  'Bug': '#FF5252',           // Red
  'Task': '#FFBB28',           // Yellow/Orange
  'Test Case': '#FF8042'      // Orange
};

export const PRIORITY_COLORS = {
  'P1': '#D32F2F',  // Strong Red
  'P2': '#F57C00',  // Orange
  'P3': '#388E3C',  // Green
  'P4': '#1976D2'   // Blue
};

const STATE_COLORS = {
  'Done': '#00C853',
  'Closed': '#2E7D32',
  'Resolved': '#4CAF50',
  'Completed': '#43A047',
  'Active': '#E91E63',
  'In Progress': '#2196F3',
  'New': '#9E9E9E',
  'To Do': '#757575',
  'Removed': '#FF1744'
};

// Scope-change chip styling. "Moved out" = iteration changed to another sprint;
// "Removed" = removed after start; "Deleted" = deleted in ADO.
const SCOPE_CHANGE = {
  planned: { label: 'Planned', color: '#4CAF50' },
  added:   { label: 'Added',   color: '#2196F3' },
  missed:  { label: 'Missed',  color: '#FF6B00' },
  removed: { label: 'Removed', color: '#c62828' },
  deleted: { label: 'Deleted', color: '#616161' },
  moved:   { label: 'Moved out', color: '#9C27B0' },
};
const COMPLETED_STATES_SC = ['Done', 'Closed', 'Resolved', 'Completed'];

// Single source of truth for an item's scope-change status in the work-items table.
export const computeScopeChange = (item, sprintEnded) => {
  if (item.deleted === true) return 'deleted';
  const base = item.scopeStatus || (item.scopeCreep ? 'added' : 'planned');
  if (base === 'moved') return 'moved';     // moved out of the iteration
  if (base === 'removed') return 'removed';  // removed after start
  // Missed only applies once the sprint has ended (kpiMissed snapshot, else heuristic).
  const missed = sprintEnded && (item.kpiMissed != null
    ? item.kpiMissed
    : (base === 'planned' || base === 'added') && !COMPLETED_STATES_SC.includes(item.state));
  if (missed) return 'missed';
  return base; // planned or added
};

// Generate ADO work item URL
const getAdoWorkItemUrl = (workItemId) => {
  const adoOrg = process.env.REACT_APP_AZURE_DEVOPS_ORG || 'tr-legal.visualstudio.com';
  const projectName = process.env.REACT_APP_AZURE_DEVOPS_PROJECT || 'HighQ Tech';
  // Handle both dev.azure.com and visualstudio.com formats
  const baseUrl = adoOrg.includes('visualstudio.com') ? `https://${adoOrg}` : `https://dev.azure.com/${adoOrg}`;
  return `${baseUrl}/${encodeURIComponent(projectName)}/_workitems/edit/${workItemId}`;
};

const getStateColor = (state) => STATE_COLORS[state] || '#6c757d';
const getPriorityColor = (priority) => PRIORITY_COLORS[`P${priority}`] || '#6c757d';

const MultiFilter = ({ label, value, onChange, options }) => (
  <FormControl fullWidth size="small">
    <InputLabel>{label}</InputLabel>
    <Select
      multiple
      value={value}
      onChange={(e) => onChange(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
      input={<OutlinedInput label={label} />}
      renderValue={(selected) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selected.map((v) => <Chip key={v} label={options.find((o) => o.value === v)?.label || v} size="small" />)}
        </Box>
      )}
    >
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          <Checkbox checked={value.indexOf(o.value) > -1} />
          <ListItemText primary={o.label} />
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const SCOPE_OPTIONS = [
  { value: 'added', label: 'Added' }, { value: 'removed', label: 'Removed' }, { value: 'deleted', label: 'Deleted' },
  { value: 'planned', label: 'Planned' }, { value: 'moved', label: 'Moved out' }, { value: 'missed', label: 'Missed' },
];

/**
 * Sprint Work Items (PBIs & Bugs) with type / priority / scope-change / status filters.
 * Used by the Sprint Reports dashboard and, collapsible, by the Sprint Review team view.
 *
 * props:
 *  - workItems, sprintEnded, scopeMetrics (optional)
 *  - onAddToReport / isInReport / locked: "add to Delivered Items" action (legacy Sprint Review only)
 *  - collapsible, defaultExpanded, onFirstExpand, loading: collapsed panel that loads on first open
 */
const SprintWorkItemsTable = ({
  workItems = [], sprintEnded = false, scopeMetrics = null,
  onAddToReport = null, isInReport = () => false, locked = false,
  collapsible = false, defaultExpanded = true, onFirstExpand = null, loading = false, sx,
}) => {
  const [expanded, setExpanded] = useState(collapsible ? defaultExpanded : true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [typeFilter, setTypeFilter] = useState([]);
  const [priorityFilter, setPriorityFilter] = useState([]);
  const [scopeChangeFilter, setScopeChangeFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);

  const visibleItems = useMemo(() => workItems.filter((i) => i.workItemType !== 'Task' && i.workItemType !== 'Test Case'), [workItems]);
  const uniq = (fn) => [...new Set(visibleItems.map(fn))].sort().map((v) => ({ value: v, label: v }));

  const filteredWorkItems = visibleItems.filter((item) => (
    (!typeFilter.length || typeFilter.includes(item.workItemType))
    && (!priorityFilter.length || priorityFilter.includes(`P${item.priority}`))
    && (!scopeChangeFilter.length || scopeChangeFilter.includes(computeScopeChange(item, sprintEnded)))
    && (!statusFilter.length || statusFilter.includes(item.state))
  ));

  const setFilter = (setter) => (v) => { setter(v); setPage(0); };
  const clearAllFilters = () => {
    setTypeFilter([]); setPriorityFilter([]); setScopeChangeFilter([]); setStatusFilter([]); setPage(0);
  };

  const toggle = () => {
    if (!expanded && onFirstExpand) onFirstExpand();
    setExpanded((v) => !v);
  };

  const header = (
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 1, ...(collapsible && { cursor: 'pointer', userSelect: 'none' }) }}
      {...(collapsible && {
        role: 'button', tabIndex: 0, 'aria-expanded': expanded, onClick: toggle,
        onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } },
      })}
    >
      {collapsible && <ExpandMoreIcon sx={{ transition: 'transform .2s', transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)', color: 'text.secondary' }} />}
      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: collapsible ? '1rem' : undefined }}>
        Sprint Work Items (PBIs &amp; Bugs)
      </Typography>
      {collapsible && workItems.length > 0 && <Chip size="small" label={visibleItems.length} />}
      {collapsible && !expanded && <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>Click to show the items in this sprint</Typography>}
    </Box>
  );

  return (
    <Paper elevation={0} sx={{ p: collapsible ? 2.5 : 3, mt: collapsible ? 0 : 4, borderRadius: collapsible ? '12px' : 3, border: '1px solid #e0e0e0', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', ...sx }}>
      <Box sx={{ mb: expanded ? 3 : 0 }}>
        {header}
        {expanded && scopeMetrics && scopeMetrics.scopeFinalized && (
          <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
            <Chip icon={<TrendingUpIcon />} label={`Scope Added: +${scopeMetrics.scopeAddedStoryPoints} Story Points`} color="info" variant="outlined" sx={{ fontWeight: 'medium' }} />
            <Chip icon={<TrendingUpIcon />} label={`Scope Removed: -${scopeMetrics.scopeRemovedStoryPoints} Story Points`} color="error" variant="outlined" sx={{ fontWeight: 'medium' }} />
          </Box>
        )}
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {loading ? (
          <Typography variant="body2" color="text.secondary">Loading sprint work items…</Typography>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={2.5}><MultiFilter label="Type" value={typeFilter} onChange={setFilter(setTypeFilter)} options={uniq((i) => i.workItemType)} /></Grid>
              <Grid item xs={12} sm={6} md={2.5}><MultiFilter label="Priority" value={priorityFilter} onChange={setFilter(setPriorityFilter)} options={uniq((i) => `P${i.priority}`)} /></Grid>
              <Grid item xs={12} sm={6} md={2.5}><MultiFilter label="Scope Change" value={scopeChangeFilter} onChange={setFilter(setScopeChangeFilter)} options={SCOPE_OPTIONS} /></Grid>
              <Grid item xs={12} sm={6} md={2.5}><MultiFilter label="Status" value={statusFilter} onChange={setFilter(setStatusFilter)} options={uniq((i) => i.state)} /></Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Button variant="outlined" onClick={clearAllFilters} size="small" fullWidth sx={{ height: '40px', borderColor: '#e0e0e0', color: '#666', '&:hover': { borderColor: '#ccc', bgcolor: '#f5f5f5' } }}>
                  Clear Filters
                </Button>
              </Grid>
            </Grid>

            <TableContainer sx={{ border: '1px solid #eee', borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                    {['Scope Change', 'ID', 'Title', 'Priority', 'Story Points', 'Fix Version', 'Test Linked', 'Status'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 600, color: '#444' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredWorkItems.length === 0 && (
                    <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary">No work items match.</Typography></TableCell></TableRow>
                  )}
                  {filteredWorkItems
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((item) => {
                      const sc = SCOPE_CHANGE[computeScopeChange(item, sprintEnded)] || { label: '-', color: '#ccc' };
                      return (
                        <TableRow key={item.id} hover sx={{ '&:hover': { bgcolor: '#fcfcfc' } }}>
                          <TableCell>
                            <Chip label={sc.label} size="small" sx={{ backgroundColor: `${sc.color}15`, color: sc.color, border: `1px solid ${sc.color}30`, fontWeight: 600, minWidth: 70 }} />
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              {onAddToReport && (
                                <Tooltip title={locked ? 'Sprint is closed (locked)' : (isInReport(item.id) ? 'Already in sprint report' : 'Add to sprint report')}>
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => onAddToReport(item)}
                                      disabled={isInReport(item.id) || locked}
                                      sx={{ p: 0.25, color: (isInReport(item.id) || locked) ? '#bbb' : '#2e7d32' }}
                                    >
                                      <AddCircleOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}
                              <Link
                                href={getAdoWorkItemUrl(item.id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                underline="none"
                                sx={{ fontWeight: '600', color: WORK_ITEM_COLORS[item.workItemType] || '#8884d8', '&:hover': { opacity: 0.8, textDecoration: 'underline' } }}
                              >
                                {item.id}
                              </Link>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 400, wordWrap: 'break-word' }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', color: '#333' }}>{item.title}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={`P${item.priority}`} size="small" sx={{ backgroundColor: `${getPriorityColor(item.priority)}20`, color: getPriorityColor(item.priority), fontWeight: 600, height: 24 }} />
                          </TableCell>
                          <TableCell><Typography variant="body2" sx={{ fontWeight: 600, color: '#555' }}>{item.storyPoints || '-'}</Typography></TableCell>
                          <TableCell><Typography variant="body2" sx={{ color: '#666' }}>{item.fixVersionDummy || '-'}</Typography></TableCell>
                          <TableCell>
                            {item.testedByCount > 0 ? (
                              <Chip label={item.testedByCount} size="small" sx={{ backgroundColor: '#2196F3', color: 'white', fontWeight: 600, height: 24, minWidth: 40 }} />
                            ) : (
                              <Typography variant="body2" sx={{ color: '#999' }}>-</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip label={item.state} size="small" sx={{ backgroundColor: getStateColor(item.state), color: 'white', fontWeight: 500, height: 24 }} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredWorkItems.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            />
          </>
        )}
      </Collapse>
    </Paper>
  );
};

export default SprintWorkItemsTable;
